import { useEffect, useState } from 'react'
import { cyclesService, CyclePair, CycleDetail } from '@/services/cycles.service'
import { toast } from 'sonner'
import { HedgePairView } from '@/components/Cycles/HedgePairView'

const fmtMoney = (v: number | null | undefined, currency = '$') => {
  if (v === null || v === undefined) return '—'
  const sign = v >= 0 ? '' : '-'
  return `${sign}${currency}${Math.abs(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
const fmtPct = (v: number | null | undefined) => v === null || v === undefined ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`
const fmtDate = (s: string | null) => {
  if (!s) return '—'
  try { return new Date(s).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }
  catch { return s }
}
const eaAgeLabel = (s: string | null): { label: string; tone: 'live' | 'stale' | 'offline' | 'none' } => {
  if (!s) return { label: 'mai visto', tone: 'none' }
  const ageSec = (Date.now() - new Date(s).getTime()) / 1000
  if (ageSec <= 15) return { label: `${Math.round(ageSec)}s fa`, tone: 'live' }
  if (ageSec <= 120) return { label: `${Math.round(ageSec)}s fa`, tone: 'stale' }
  if (ageSec < 3600) return { label: `${Math.round(ageSec / 60)} min fa`, tone: 'offline' }
  return { label: `${Math.round(ageSec / 3600)} h fa`, tone: 'offline' }
}

export default function MyCycles() {
  const [pairs, setPairs] = useState<CyclePair[]>([])
  const [loadingList, setLoadingList] = useState(true)
  const [selectedPairId, setSelectedPairId] = useState<number | null>(null)
  const [detail, setDetail] = useState<CycleDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [now, setNow] = useState(Date.now())

  // Refresh lista coppie ogni 10s
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const data = await cyclesService.getMyPairs()
        if (cancelled) return
        setPairs(data)
        if (selectedPairId === null && data.length > 0) {
          setSelectedPairId(data[0].pair_id)
        }
      } catch (e) {
        if (!cancelled) toast.error('Errore nel caricamento delle coppie')
      } finally {
        if (!cancelled) setLoadingList(false)
      }
    }
    load()
    const t = setInterval(load, 10_000)
    return () => { cancelled = true; clearInterval(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Detail al cambio selezione + polling 10s
  useEffect(() => {
    if (selectedPairId === null) { setDetail(null); return }
    let cancelled = false
    const load = async () => {
      setLoadingDetail(true)
      try {
        const d = await cyclesService.getMyPairDetail(selectedPairId)
        if (!cancelled) setDetail(d)
      } catch {
        if (!cancelled) { setDetail(null); toast.error('Errore nel caricamento del dettaglio') }
      } finally {
        if (!cancelled) setLoadingDetail(false)
      }
    }
    load()
    const t = setInterval(load, 10_000)
    return () => { cancelled = true; clearInterval(t) }
  }, [selectedPairId])

  // Tick ogni 5s per aggiornare gli "X sec fa"
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5_000)
    return () => clearInterval(t)
  }, [])
  void now

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold">I miei cicli</h1>
        <p className="text-sm text-muted-foreground">Storico operazioni e stato corrente delle tue coppie iXen.</p>
      </div>

      {loadingList ? (
        <div className="text-center py-10 text-muted-foreground">Caricamento…</div>
      ) : pairs.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center text-muted-foreground">
          Nessuna coppia iXen configurata. Vai in <span className="font-semibold">Conti</span> per crearne una.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
          {/* LISTA COPPIE */}
          <div className="space-y-2">
            {pairs.map(p => {
              const isSel = p.pair_id === selectedPairId
              const ea = eaAgeLabel(p.ea_last_seen)
              const pnlPos = (p.cycle_pnl ?? 0) >= 0
              return (
                <button
                  key={p.pair_id}
                  onClick={() => setSelectedPairId(p.pair_id)}
                  className={`w-full text-left rounded-lg border p-3 transition ${
                    isSel ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 bg-card'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{p.display_name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {p.prop_provider} · {p.prop_phase} · ${p.prop_size.toLocaleString('it-IT')}
                      </div>
                    </div>
                    {!p.is_active && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">inattiva</span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className={`text-sm font-semibold ${pnlPos ? 'text-green-500' : 'text-red-500'}`}>
                      {fmtMoney(p.cycle_pnl)} <span className="text-[11px] font-normal opacity-80">({fmtPct(p.cycle_pnl_pct)})</span>
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {p.open_ops} aperte · {p.closed_ops} chiuse
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5 text-[10px]">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                      ea.tone === 'live' ? 'bg-green-500 animate-pulse'
                        : ea.tone === 'stale' ? 'bg-amber-500'
                        : 'bg-red-500'
                    }`} />
                    <span className="text-muted-foreground">EA PROP {ea.label}</span>
                  </div>
                </button>
              )
            })}
          </div>

          {/* DETTAGLIO */}
          <div className="border border-border rounded-lg bg-card min-h-[400px]">
            {loadingDetail && !detail ? (
              <div className="p-10 text-center text-muted-foreground">Caricamento dettaglio…</div>
            ) : !detail ? (
              <div className="p-10 text-center text-muted-foreground">Seleziona una coppia per vedere il dettaglio.</div>
            ) : (
              <div className="p-4 space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <h2 className="text-lg font-bold">{detail.pair.display_name}</h2>
                    <div className="text-xs text-muted-foreground">
                      {detail.pair.prop_provider} · {detail.pair.prop_phase} · ${detail.pair.prop_size.toLocaleString('it-IT')}
                      {detail.pair.created_at && <> · creata {fmtDate(detail.pair.created_at)}</>}
                    </div>
                  </div>
                </div>

                {/* Vista hedge accoppiata: snapshot live + operazioni PROP/BROKER affiancate + totali */}
                <HedgePairView
                  operations={detail.operations}
                  equity={detail.snapshot.equity}
                  balance={detail.snapshot.balance}
                  cyclePnl={detail.snapshot.cycle_pnl}
                />

                {/* Breakdown (per direzione / per sorgente) */}
                {(Object.keys(detail.stats.by_source).length > 0 || Object.keys(detail.stats.by_direction).length > 0) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="border border-border rounded p-2">
                      <div className="text-[10px] text-muted-foreground uppercase mb-1">Per direzione</div>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(detail.stats.by_direction).map(([d, n]) => (
                          <span key={d} className="text-[11px] px-2 py-0.5 rounded bg-muted">
                            {d}: <span className="font-semibold">{n}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="border border-border rounded p-2">
                      <div className="text-[10px] text-muted-foreground uppercase mb-1">Per sorgente segnale</div>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(detail.stats.by_source).map(([s, n]) => (
                          <span key={s} className="text-[11px] px-2 py-0.5 rounded bg-muted">
                            {s}: <span className="font-semibold">{n}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
